import { motion } from 'framer-motion';
import { ShieldAlert, RotateCcw, AlertTriangle, Shield } from 'lucide-react';
import { Button } from '../../../components/ui/button';

/**
 * Security & Protection section
 * - Regenerate dashboard URL (encrypted link)
 * - Placeholder for future 2FA toggle
 */
export default function SecuritySection({ language, onRegenerateClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6"
      data-testid="security-section"
    >
      <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
        <ShieldAlert className="h-5 w-5 text-red-400" />
        {language === 'ar' ? 'الأمان والحماية' : 'Security & Protection'}
      </h2>

      <div className="space-y-6">
        {/* Regenerate dashboard URL */}
        <div className="bg-white/[0.02] rounded-2xl border border-white/10 p-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-red-500/10 rounded-xl flex items-center justify-center shrink-0 border border-red-500/20">
              <RotateCcw className="h-7 w-7 text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-white mb-2">
                {language === 'ar' ? 'إعادة تشفير رابط لوحة التحكم' : 'Regenerate Dashboard URL'}
              </h3>
              <p className="text-[#71717A] text-sm mb-4">
                {language === 'ar'
                  ? 'سيتم إنشاء رابط مشفر جديد للوحة التحكم وإرسال الرابط الجديد عبر البريد الإلكتروني لجميع المشرفين المسجلين. الرابط القديم لن يعمل بعد التغيير.'
                  : 'A new encrypted URL will be generated and sent via email to all registered admins. The old URL will no longer work.'}
              </p>
              <div className="flex items-center gap-3 p-3 bg-amber-500/10 rounded-xl border border-amber-500/20 mb-4">
                <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0" />
                <p className="text-amber-400 text-xs">
                  {language === 'ar'
                    ? 'تحذير: هذا الإجراء سيؤدي إلى تسجيل خروج جميع المستخدمين وسيتطلب منهم استخدام الرابط الجديد.'
                    : 'Warning: This action will log out all users and require them to use the new URL.'}
                </p>
              </div>
              <Button
                onClick={onRegenerateClick}
                className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30"
                data-testid="btn-open-regenerate-url-modal"
              >
                <RotateCcw className="h-4 w-4 ml-2" />
                {language === 'ar' ? 'إعادة تشفير الرابط' : 'Regenerate URL'}
              </Button>
            </div>
          </div>
        </div>

        {/* 2FA placeholder */}
        <div className="bg-white/[0.02] rounded-2xl border border-white/10 p-6 opacity-60">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center shrink-0 border border-blue-500/20">
              <Shield className="h-7 w-7 text-blue-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-white">
                  {language === 'ar' ? 'التحقق بخطوتين' : 'Two-Factor Authentication'}
                </h3>
                <span className="px-2 py-0.5 bg-white/5 text-[#71717A] text-xs rounded-full">
                  {language === 'ar' ? 'قريباً' : 'Soon'}
                </span>
              </div>
              <p className="text-[#71717A] text-sm mt-2">
                {language === 'ar'
                  ? 'إضافة طبقة حماية إضافية عند تسجيل الدخول'
                  : 'Add an extra layer of security when logging in'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
